/** The three things Mochi can make from a creation chat (spec §5.8.6). */
export type CreateKind = "presentation" | "document" | "spreadsheet";

export const CREATE_KINDS: CreateKind[] = ["presentation", "document", "spreadsheet"];

export const isCreateKind = (v: unknown): v is CreateKind =>
  v === "presentation" || v === "document" || v === "spreadsheet";

/** Where the new file opens, with `?build=<runId>` so the editor follows Mochi. */
export function editorPath(kind: CreateKind, fileId: string, runId?: string | null): string {
  const base = kind === "presentation" ? "/present" : kind === "spreadsheet" ? "/grid" : "/write";
  return `${base}/${fileId}${runId ? `?build=${encodeURIComponent(runId)}` : ""}`;
}

interface KindCopy {
  appKey: string;
  app: string;
  /** "Describe the {{noun}} you want…" (design `FM_KINDS[k].noun`). */
  nounKey: string;
  noun: string;
  createKey: string;
  create: string;
  examples: { key?: string; text: string }[];
}

export const KIND_COPY: Record<CreateKind, KindCopy> = {
  presentation: {
    appKey: "mochi.create.kind.presentation",
    app: "Present",
    nounKey: "filesMochi.create.noun.presentation",
    noun: "presentation",
    createKey: "mochi.create.card.presentation",
    create: "Create a presentation with {{name}}",
    examples: [
      { key: "mochi.create.example.presentation1", text: "A 10-minute board update on Q3 results" },
      { key: "mochi.create.example.presentation2", text: "Onboarding deck for new engineers — match our brand guide" },
      { text: "عرض تقديمي عن خطة التحول الرقمي للوزارة" },
    ],
  },
  document: {
    appKey: "mochi.create.kind.document",
    app: "Write",
    nounKey: "filesMochi.create.noun.document",
    noun: "document",
    createKey: "mochi.create.card.document",
    create: "Create a document with {{name}}",
    examples: [
      { key: "mochi.create.example.document1", text: "A one-page proposal for a 3-month pilot with ACME" },
      { key: "mochi.create.example.document2", text: "Minutes template for our weekly product review" },
      { key: "filesMochi.create.example.document3", text: "A brief explaining why Q3 revenue declined" },
    ],
  },
  spreadsheet: {
    appKey: "mochi.create.kind.spreadsheet",
    app: "Grid",
    nounKey: "filesMochi.create.noun.spreadsheet",
    noun: "spreadsheet",
    createKey: "mochi.create.card.spreadsheet",
    create: "Create a spreadsheet with {{name}}",
    examples: [
      { key: "mochi.create.example.spreadsheet1", text: "A 12-month budget for a 5-person marketing team" },
      { key: "mochi.create.example.spreadsheet2", text: "Hiring tracker with stages and owners" },
      { key: "filesMochi.create.example.spreadsheet3", text: "Launch checklist with dates and status" },
    ],
  },
};

/** Ask any open creation dialog host to open (see `MochiCreateHost`). */
export const MOCHI_CREATE_EVENT = "weco:mochi-create";

export interface MochiCreateRequest {
  kind?: CreateKind;
  /** Drive item ids to pre-attach ("Create from these" in Files), ≤ 8. */
  fileIds?: string[];
}

export function openMochiCreate(kind?: CreateKind, fileIds?: string[]) {
  window.dispatchEvent(new CustomEvent<MochiCreateRequest>(MOCHI_CREATE_EVENT, { detail: { kind, fileIds } }));
}
